import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Building2, CreditCard, FileText, Users, Bed, User, Calendar,
  DollarSign, BarChart3, Calculator, ClipboardList, Clock, Home, Settings, LogOut, Menu, Shield, Megaphone, Layers, Download, Activity, Brush,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';

function ZaffTree({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="currentColor">
        <circle cx="32" cy="20" r="13.5" />
        <circle cx="19" cy="28.5" r="9.5" />
        <circle cx="45" cy="28.5" r="9.5" />
      </g>
      <path d="M32 29v27" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round" />
      <path
        d="M32 47c-4.2 0-7.5 2.7-9.5 6.5M32 47c4.2 0 7.5 2.7 9.5 6.5"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const iconMap = {
  Dashboard: LayoutDashboard,
  Building: Building2,
  Card: CreditCard,
  File: FileText,
  Users,
  Shield,
  Megaphone,
  Layers,
  Download,
  Activity,
  Bed,
  User,
  Calendar,
  Dollar: DollarSign,
  Chart: BarChart3,
  Calculator,
  Clip: ClipboardList,
  BrushIcon: Brush,
  Clock,
  Home,
  Gear: Settings,
  Out: LogOut,
  Menu,
};

const routeIconMap = {
  '/super-admin': 'Dashboard',
  '/tenants': 'Building',
  '/system-payments': 'Card',
  '/system-logs': 'File',
  '/system-health': 'Activity',
  '/security-events': 'Shield',
  '/announcements': 'Megaphone',
  '/plans': 'Layers',
  '/reports': 'Download',
  '/system-users': 'Users',
  '/dashboard': 'Dashboard',
  '/rooms': 'Bed',
  '/guests': 'User',
  '/bookings': 'Calendar',
  '/upcoming-arrivals': 'Calendar',
  '/employees': 'Users',
  '/payroll': 'Dollar',
  '/hotel-payments': 'Card',
  '/pricing': 'Dollar',
  '/accounting': 'Calculator',
  '/hotel-reports': 'Chart',
  '/attendance': 'Clock',
  '/settings': 'Gear',
  '/employee-dashboard': 'Dashboard',
  '/tasks': 'BrushIcon',
  '/schedule': 'Clock',
  '/profile': 'User',
};

const roleLabelMap = { super_admin: 'Super Admin', admin: 'Hotel Admin', staff: 'Staff' };
const roleBgMap = { super_admin: 'bg-violet-600', admin: 'bg-secondary-500', staff: 'bg-accent-green-500' };

function NavItem({ route, isActive, onClick }) {
  const iconKey = routeIconMap[route.path] || 'Dashboard';
  const Icon = iconMap[iconKey];
  return (
    <Link
      to={route.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-base font-medium transition-all duration-150
        ${isActive
      ? 'bg-primary-500 text-white shadow-md'
      : 'text-white hover:text-white hover:bg-white/[0.08]'
    }
      `}
    >
      <Icon size={18} className="shrink-0" />
      <span>{route.name}</span>
    </Link>
  );
}

function Sidebar({ onNavClick }) {
  const { user, logout, getRoleBasedRoutes } = useAuth();
  const location = useLocation();

  if (!user) { return null; }

  const routes = getRoleBasedRoutes();
  const avatarBg = roleBgMap[user.role] || 'bg-gray-500';
  const roleLabel = roleLabelMap[user.role] || 'User';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gradient-to-b from-secondary-600 to-secondary-900 text-white">
      {/* Logo + mobile close button */}
      <div className="px-4 py-4 border-b border-white/[0.07] flex items-center gap-2.5">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-ethiopian-green via-emerald-600 to-emerald-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-900/30">
          <ZaffTree className="w-7 h-7 text-white" />
        </div>
        <span className="text-white font-bold text-3xl tracking-tight flex-1">ዛፍ</span>
        {onNavClick && (
          <button
            onClick={onNavClick}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            aria-label="Close navigation menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        )}
      </div>

      {/* User */}
      <div className="px-4 py-3.5 border-b border-white/[0.07] flex items-center gap-3">
        <div className={`
          w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0
          ${avatarBg}
        `}>
          {user.firstName?.charAt(0) || 'U'}
        </div>
        <div className="min-w-0">
          <div className="text-white text-sm font-semibold truncate">{user.firstName} {user.lastName}</div>
          <div className="text-xs text-white/90 font-medium truncate">{user.tenantName || 'Your Hotel'}</div>
          <div className="text-xs text-white/60">{roleLabel}</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 p-2.5 flex flex-col gap-0.5 overflow-y-auto overscroll-contain">
        {routes.map(route => (
          <NavItem
            key={route.path}
            route={route}
            isActive={location.pathname === route.path}
            onClick={onNavClick}
          />
        ))}
      </nav>

      {/* Bottom */}
      <div className="p-2.5 border-t border-white/[0.07] flex flex-col gap-0.5">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-white/90 hover:text-white hover:bg-white/[0.08] transition-colors w-full"
        >
          <LogOut size={18} className="shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

function VerticalSidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, getRoleBasedRoutes } = useAuth();
  const location = useLocation();

  if (!user) { return null; }

  const routes = getRoleBasedRoutes();

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-64 z-50 flex-col">
        <Sidebar onNavClick={() => setMobileOpen(false)} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[200] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sliding sidebar */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 w-64 z-[210] md:hidden transition-transform duration-250 ease-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <Sidebar onNavClick={() => setMobileOpen(false)} />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white shadow-sm z-[150] flex items-center px-3 gap-2">
        <button
          onClick={() => setMobileOpen(true)}
          className="w-11 h-11 flex items-center justify-center text-secondary-700 hover:text-primary-600 hover:bg-secondary-50 rounded-lg transition-colors shrink-0"
          aria-label="Open navigation menu"
        >
          <Menu size={22} />
        </button>
        <span className="font-bold text-base text-secondary-900 truncate">
          {routes.find(r => r.path === location.pathname)?.name || 'Zaff Hotel Platform'}
        </span>
      </header>
    </>
  );
}

export default VerticalSidebar;
